import React, { useState, useEffect, useMemo } from 'react';

import './emails.css';
import getEmailCampaignsList from '../../../api/graphql/queries/getEmailCampaignsList';
import { campaignMatchesFilter, compareCampaignsByNewest, KIND_LABELS } from '../../../utils/emailPlates';
import languages from '../../../utils/languages';
import LoadIcon from '../../LoadIcon';
import Modal from '../../Modal/Modal';
import EmailCampaignList from './EmailCampaignList';
import EmailCampaignCreateModal from './EmailCampaignCreateModal';

// Emails tab, screen 1 of the approved design (docs/superpowers/design/
// 2026-08-31-email-campaigns-mockup.html): one row per marketing-scope
// "campaign", with a per-language plate strip showing which languages have
// their own email.
//
// The design's toolbar also carries a Brand switcher, which is not here and will
// not be: the admin is deployed per brand (PS and RDL run separate stacks off the
// same code), so there is nothing to switch between.
//
// "New campaign" IS here, as a modal rather than the design's /cms/emails/new
// route. That path is three segments and `/cms/:tab/:postCategory` already matches
// it as `exact`, so <Switch> would render this tab and silently ignore the create
// screen — the trap documented at src/shared/routes.js:63.
const EmailsContainer = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOn, setIsCreateModalOn] = useState(false);

  // Filtering items:
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);

  useEffect(() => {
    console.log('Calling getEmailCampaignsList()');
    getEmailCampaignsList({}).then((campaignsDataObject) => {
      if (campaignsDataObject) {
        if (campaignsDataObject.isAuth) {
          setCampaigns(campaignsDataObject.campaigns || []);
          setIsLoading(false);
        } else {
          console.log('getEmailCampaignsList failed!');
        }
      } else {
        console.log('campaignsData = null');
      }
    });
    // Fetch once on mount. There are only ~56 campaigns, so kind/search/active
    // filtering is done client-side below (campaignMatchesFilter) rather than
    // round-tripping to the server on every keystroke or toggle.
  }, []);

  // Sorted HERE, on the way to the table, rather than when a campaign is added.
  //
  // getEmailCampaigns does not sort, so the rows arrive in Mongo's natural order.
  // That looks alphabetical only because the seed inserted them that way, and it
  // is not: scope_TEST_SPACE sits second, ahead of scope_asu. Sorting only the
  // post-create array would therefore make the whole list reshuffle the moment you
  // created something — rows moving for no reason the user can see. One comparator
  // in one place instead, so the order is the same before and after.
  //
  // Newest first. Alphabetical put the campaign you just made wherever its name
  // fell, which for a list this long meant scrolling to find out whether it had
  // been created at all. The thing you are most likely to want is the thing you
  // most recently made, so it sits at the top.
  //
  // `.slice()` first: this array came out of an Apollo result and is frozen, and
  // `.sort()` mutates in place. __DEV__ is undefined in these bundles, so the
  // freeze is real in every build, not just dev.
  const filteredCampaigns = useMemo(
    () => campaigns
      .filter(campaign => campaignMatchesFilter(campaign, { kind, activeOnly, search }))
      .slice()
      .sort(compareCampaignsByNewest),
    [campaigns, kind, activeOnly, search],
  );

  const handleCreated = (campaign) => {
    setCampaigns(previous => [...previous, campaign]);
    setIsCreateModalOn(false);
  };

  return (
    <div className="emails-app">
      <div className="headline">
        <h2>Emails</h2>
        <span className="count">
          {`${filteredCampaigns.length} / ${campaigns.length} campaigns`}
        </span>
      </div>

      {isLoading ? (
        <div className="emails-loading">
          <LoadIcon />
          Loading campaigns…
        </div>
      ) : (
        <>
          <div className="toolbar">
            <div className="field grow">
              <label htmlFor="emails-search-input">
                Search
                <span className="control wide hasinput">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ color: 'rgba(0,0,0,.38)' }}>
                    <circle cx="7" cy="7" r="4.6" />
                    <path d="M10.4 10.4L14 14" strokeLinecap="round" />
                  </svg>
                  <input
                    id="emails-search-input"
                    type="text"
                    placeholder="Campaign, scope or subject"
                    value={search}
                    onChange={e => setSearch(e.currentTarget.value)}
                  />
                </span>
              </label>
            </div>

            <div className="field">
              <label htmlFor="emails-kind-select">
                Kind
                <select
                  id="emails-kind-select"
                  value={kind}
                  onChange={e => setKind(e.currentTarget.value)}
                >
                  <option value="">All</option>
                  {Object.keys(KIND_LABELS).map(kindKey => (
                    <option key={kindKey} value={kindKey}>{KIND_LABELS[kindKey]}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="checkfield">
              <label htmlFor="emails-active-only-checkbox">
                <input
                  id="emails-active-only-checkbox"
                  type="checkbox"
                  checked={activeOnly}
                  onChange={e => setActiveOnly(e.currentTarget.checked)}
                />
                Active only
              </label>
            </div>

            <button
              type="button"
              className="btn"
              style={{ marginLeft: 'auto' }}
              onClick={() => setIsCreateModalOn(true)}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.9">
                <path d="M6 1.5v9M1.5 6h9" strokeLinecap="round" />
              </svg>
              New campaign
            </button>
          </div>

          {filteredCampaigns.length === 0 ? (
            <div className="emails-empty">No campaigns match this filter.</div>
          ) : (
            <EmailCampaignList
              campaigns={filteredCampaigns}
              languages={languages}
              totalCount={campaigns.length}
            />
          )}
        </>
      )}

      {isCreateModalOn && (
        <Modal
          title="New campaign"
          handleClick={() => setIsCreateModalOn(false)}
          type="createCampaign"
        >
          <EmailCampaignCreateModal
            campaigns={campaigns}
            onCreated={handleCreated}
            onClose={() => setIsCreateModalOn(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default EmailsContainer;
